import React, { forwardRef, Ref, useEffect, useImperativeHandle, useRef } from 'react';
import { Tooltip as TooltipOriginal, TooltipProps } from 'primereact/tooltip';
import { classNames } from 'primereact/utils';
import './Tooltip.scss';

type TooltipVariant = 'default' | 'white';

export type AppTooltipProps = TooltipProps & {
	variant?: TooltipVariant;
	openOnClick?: boolean;
	slim?: boolean;
};

export const Tooltip = forwardRef(
	(
		{
			variant = 'default',
			openOnClick = false,
			slim = false,
			className,
			showEvent,
			hideEvent,
			...restProps
		}: AppTooltipProps,
		ref: Ref<TooltipOriginal>
	) => {
		const tooltipRef = useRef<TooltipOriginal>(null);
		useImperativeHandle(ref, () => tooltipRef.current as TooltipOriginal);

		useEffect(() => {
			if (!openOnClick) {
				return undefined;
			}

			const handleOutsideClick = (event: MouseEvent) => {
				const tooltipInstance = tooltipRef.current;
				if (!tooltipInstance) {
					return;
				}

				const targetElement = tooltipInstance.getTarget();
				if (targetElement?.contains(event.target as Node)) {
					return;
				}

				tooltipInstance.hide();
			};

			document.addEventListener('click', handleOutsideClick);
			return () => {
				document.removeEventListener('click', handleOutsideClick);
			};
		}, [openOnClick]);

		return (
			<TooltipOriginal
				ref={tooltipRef}
				autoHide
				className={classNames(className, {
					'app-tooltip': true,
					'tooltip-white': variant === 'white',
					'tooltip-slim': slim,
				})}
				showEvent={openOnClick && !showEvent ? 'click' : showEvent}
				hideEvent={openOnClick && !hideEvent ? 'click' : hideEvent}
				{...restProps}
			/>
		);
	}
);
